import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, Clock, Send, Check } from "lucide-react";
import { useSection, useSettings } from "../lib/useCMS";
import { PageShell } from "../components/PageShell";
import { PageHero } from "../components/page/PageHero";
import { SEO } from "../components/page/SEO";
import { trackConversion, EVENTS } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const p = useSection("page_contact", {});
  const settings = useSettings({ contact_email: "mike@builtdifferenthockey.com", instagram_url: "", square_booking_url: "" });
  const [form, setForm] = useState({ name: "", email: "", player_level: "", website: "" });
  const [state, setState] = useState("idle");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setState("loading");
    try {
      // Contact form posts to the inquiries endpoint so the visitor receives
      // the on-brand "Thanks — we'll be in touch" auto-responder (instead of
      // the newsletter welcome) and Mike gets a properly framed lead email.
      // The `player_level` field on the contact page captures whatever the
      // visitor typed ("14U AAA D-man looking for off-season training") so we
      // route it into both the `message` body (for context) and `program`
      // (for the email subject line), and label the source as the contact
      // form so it's distinguishable in the admin Inquiries dashboard.
      const userContext = form.player_level.trim();
      await axios.post(`${API}/cms/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: "",
        program: "General Inquiry",
        program_slug: "",
        message: userContext,
        source_path: "/contact",
        website: form.website,
      });
      setState("success");
      trackConversion(EVENTS.CONTACT_FORM_SUBMIT, {
        method: "contact_page",
      });
      toast.success("Got it. We'll be in touch.");
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    }
  };

  return (
    <PageShell>
      <SEO title={p.meta_title} description={p.meta_description} canonical="/contact" />
      <PageHero overline={p.hero_overline} heading={p.hero_heading} subheading={p.hero_subheading} />

      <section className="bg-[#0a0a0a] py-16 md:py-20 border-b border-white/5">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5 space-y-6">
            <div className="bento-card p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light flex items-center gap-2">
                <Mail className="h-3.5 w-3.5" /> {p.email_label || "Email"}
              </div>
              <a href={`mailto:${p.email_value || settings.contact_email}`} className="font-display uppercase text-lg sm:text-xl mt-3 inline-block hover:text-brand-light transition-colors" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                {p.email_value || settings.contact_email}
              </a>
            </div>
            <div className="bento-card p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" /> {p.response_time_label || "Response Time"}
              </div>
              <p className="text-zinc-300 mt-3 text-sm leading-relaxed">{p.response_time_value || "Within 1 business day."}</p>
            </div>
            <div className="bento-card p-6 bg-brand border-brand">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/80">{p.booking_label || "Already Know What You Need?"}</div>
              <p className="text-white/90 mt-3 text-sm leading-relaxed">{p.booking_body}</p>
              {(p.booking_cta_url || settings.square_booking_url) && (
                <a href={p.booking_cta_url || settings.square_booking_url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 bg-white text-brand font-display uppercase tracking-wider px-4 py-2.5 text-xs hover:bg-zinc-200 transition-colors">
                  {p.booking_cta_label || "Book on Square"} <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </div>

          <form onSubmit={onSubmit} className="md:col-span-7 bento-card p-6 md:p-8" data-testid="contact-form">
            {/* Honeypot — hidden from humans, visible to bots. Must stay empty. */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="hidden"
              aria-hidden="true"
            />
            {state === "success" ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="h-16 w-16 bg-brand grid place-items-center mb-6"><Check className="h-8 w-8 text-white" /></div>
                <div className="font-display text-3xl md:text-4xl uppercase leading-[0.9]">Message received.</div>
                <p className="text-zinc-400 mt-4 max-w-sm">We'll be in touch within 1 business day. In the meantime, check your inbox.</p>
              </div>
            ) : (
              <>
                <p className="overline">Send a Message</p>
                <h2 className="font-display text-3xl md:text-4xl uppercase mt-3 leading-tight">Tell us about the player.</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
                  <label className="flex flex-col gap-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">Name</span>
                    <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-3 text-white placeholder:text-zinc-600 transition-colors" placeholder="Player or parent name" />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">Email</span>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-3 text-white placeholder:text-zinc-600 transition-colors" placeholder="you@domain.com" />
                  </label>
                  <label className="flex flex-col gap-2 md:col-span-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">Player Level / What you're looking for</span>
                    <input type="text" value={form.player_level} onChange={(e) => setForm({ ...form, player_level: e.target.value })} className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-3 text-white placeholder:text-zinc-600 transition-colors" placeholder="e.g. 14U AAA D-man looking for off-season training" />
                  </label>
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                  <p className="text-xs text-zinc-500 max-w-md">We'll be in touch within one business day.</p>
                  <button type="submit" disabled={state === "loading"} className="btn-primary disabled:opacity-70">
                    {state === "loading" ? (<><Loader2 className="h-5 w-5 animate-spin" /> Sending</>) : (<>Send Message <Send className="h-4 w-4" /></>)}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </section>
    </PageShell>
  );
}
